import React from 'react';
import { AttentionItem, GmailMessage, formatRelativeTime } from '../../domain/types';
import { Avatar, Badge, Button } from '../../ui/common';

function isSentByMe(msg: GmailMessage, currentUserEmail?: string): boolean {
  if (currentUserEmail && msg.from.email.toLowerCase() === currentUserEmail.toLowerCase()) {
    return true;
  }
  const email = msg.from.email.toLowerCase();
  const name = msg.from.name.toLowerCase();
  return (
    email.startsWith('ti@') ||
    email.startsWith('yo@') ||
    name === 'juan chez' ||
    name === 'tú' ||
    name === 'yo'
  );
}

function GmailItemSummary({
  item,
  busy,
  onGenerateSummary,
}: {
  item: AttentionItem;
  busy: boolean;
  onGenerateSummary: () => void;
}) {
  return (
    <div
      data-testid="gmail-item-summary"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 11,
        background: 'var(--z-accent-wash)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--z-accent-ink)' }}>
          Resumen del ítem
        </span>
        <button
          type="button"
          data-testid="gmail-summary-refresh"
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            onGenerateSummary();
          }}
          style={{
            marginLeft: 'auto',
            height: 24,
            padding: '0 10px',
            borderRadius: 8,
            border: 'none',
            cursor: busy ? 'wait' : 'pointer',
            fontSize: 11,
            fontWeight: 600,
            background: 'var(--z-accent-soft)',
            color: 'var(--z-accent-ink)',
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Generando…' : 'Actualizar resumen'}
        </button>
      </div>
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--z-muted-3)' }}>
        {item.ai}
      </p>
    </div>
  );
}

function GmailThreadMessageRow({ msg, ownMessage }: { msg: GmailMessage; ownMessage: boolean }) {
  return (
    <div
      data-testid="gmail-thread-message"
      data-message-id={msg.id}
      data-is-own={ownMessage ? 'true' : 'false'}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '11px 13px',
        borderRadius: 12,
        background: ownMessage
          ? 'var(--z-accent-wash)'
          : msg.unread
            ? 'var(--z-warn-bg)'
            : 'var(--z-fill)',
        border: 'none',
        marginLeft: ownMessage ? 20 : 0,
        marginRight: ownMessage ? 0 : 20,
      }}
    >
      <Avatar
        initials={msg.from.initials || (ownMessage ? 'TÚ' : 'GM')}
        color={msg.from.color || (ownMessage ? 'var(--z-accent)' : 'var(--z-muted-2)')}
        size={28}
      />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>
            {msg.from.name || msg.from.email}
          </span>
          {ownMessage && <Badge label="Enviado por ti" tone="accent" />}
          {!ownMessage && msg.unread && <Badge label="No leído" tone="warn" />}
          <span style={{ fontSize: 11, color: 'var(--z-faint)' }}>
            {formatRelativeTime(msg.receivedAt)}
          </span>
          {msg.to && (
            <span style={{ fontSize: 11, color: 'var(--z-dim)', marginLeft: 'auto' }}>
              Para: {msg.to}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(msg.bodyParagraphs || []).map((paragraph, pIdx) => (
            <p
              key={pIdx}
              style={{
                margin: 0,
                fontSize: 12.5,
                lineHeight: 1.55,
                color: 'var(--z-text-3)',
                textWrap: 'pretty',
              }}
            >
              {paragraph}
            </p>
          ))}
        </div>

        {msg.quotedText && (
          <blockquote
            style={{
              margin: '8px 0 0',
              padding: '6px 10px',
              borderRadius: 8,
              background: 'var(--z-fill-2)',
              color: 'var(--z-dim)',
              fontSize: 11.5,
              lineHeight: 1.45,
            }}
          >
            {msg.quotedHead && (
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{msg.quotedHead}</div>
            )}
            {msg.quotedText}
          </blockquote>
        )}
      </div>
    </div>
  );
}

interface GmailCardExpandedProps {
  item: AttentionItem;
  threadMessages: GmailMessage[];
  currentUserEmail?: string;
  linkedTicket?: AttentionItem;
  onNavigateToJira?: (id: string) => void;
  replyDraft: string;
  onReplyDraftChange: (value: string) => void;
  onSend: (e: React.MouseEvent) => void;
  onGenerateDraft?: (e: React.MouseEvent) => void;
  isSending?: boolean;
  isDrafting?: boolean;
  sendError?: string | null;
  summaryBusy: boolean;
  onGenerateSummary: () => void;
}

export const GmailCardExpanded: React.FC<GmailCardExpandedProps> = ({
  item,
  threadMessages,
  currentUserEmail,
  linkedTicket,
  onNavigateToJira,
  replyDraft,
  onReplyDraftChange,
  onSend,
  onGenerateDraft,
  isSending = false,
  isDrafting = false,
  sendError,
  summaryBusy,
  onGenerateSummary,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
    <GmailItemSummary item={item} busy={summaryBusy} onGenerateSummary={onGenerateSummary} />

    {/* Thread messages scrollback (matching Slack layout) */}
    <div
      data-testid="gmail-thread-scrollback"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      {threadMessages.map((msg, idx) => (
        <GmailThreadMessageRow
          key={msg.id || idx}
          msg={msg}
          ownMessage={isSentByMe(msg, currentUserEmail)}
        />
      ))}
    </div>

    {linkedTicket && (
      <button
        onClick={(event) => {
          event.stopPropagation();
          onNavigateToJira?.(linkedTicket.id);
        }}
        style={{
          alignSelf: 'flex-start',
          border: 'none',
          background: 'var(--z-accent-wash)',
          color: 'var(--z-accent-ink)',
          borderRadius: 999,
          padding: '5px 11px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Abrir {linkedTicket.ticket}
      </button>
    )}

    {/* Reply composer area */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '12px 13px',
        borderRadius: 12,
        background: 'var(--z-fill)',
      }}
    >
      <span style={{ fontSize: 11, color: 'var(--z-faint)' }}>
        Para: {threadMessages[threadMessages.length - 1]?.from.email || item.fromEmail}
      </span>
      <textarea
        data-testid="gmail-reply-composer"
        value={replyDraft}
        onChange={(event) => onReplyDraftChange(event.target.value)}
        onClick={(event) => event.stopPropagation()}
        placeholder="Escribe una respuesta o una idea para el borrador…"
        disabled={isSending || isDrafting}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          minHeight: 54,
          padding: '7px 9px',
          borderRadius: 8,
          border: 'none',
          background: 'var(--z-fill-2)',
          fontFamily: 'inherit',
          fontSize: 12.5,
          color: 'var(--z-text-3)',
          resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {onGenerateDraft ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid="gmail-generate-draft"
            onClick={onGenerateDraft}
            disabled={!replyDraft.trim() || isDrafting || isSending}
            title={
              replyDraft.trim()
                ? 'Expandir tu idea con el tono y contexto del correo'
                : 'Escribe primero tu idea o intención en el compositor'
            }
          >
            {isDrafting ? 'Generando…' : 'Generar borrador'}
          </Button>
        ) : null}
        <Button
          variant="primary"
          size="sm"
          onClick={onSend}
          disabled={!replyDraft.trim() || isSending || isDrafting}
        >
          {isSending ? 'Enviando…' : 'Enviar respuesta'}
        </Button>
      </div>
      {sendError ? (
        <p
          role="alert"
          style={{ margin: 0, fontSize: 11.5, lineHeight: 1.45, color: 'var(--z-bad)' }}
        >
          {sendError}
        </p>
      ) : null}
    </div>
  </div>
);
